// The icon bar at the top of a task group's details panel (S128), the one thing left when the
// panel is folded, like the collapsed rail: the VM and Workspace terminals, Refresh tasks now,
// the VPN's state (a dot on the shield) opening the VPN settings, and the group's network
// activity. Each shows only where the API would allow it: terminals and VPN for the group's
// owner or a Super Admin (D79), the refresh for the owner.
import { Cube, GlobeHemisphereWest, ShieldCheck, Terminal } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";

import { accessQuery } from "../../auth/access";
import { useT } from "../../i18n";
import { cn } from "../../ui/cn";
import { environmentVpnQuery, type VpnState, vpnState } from "../../vpn/api";
import { environmentWorkspacesQuery } from "../../workloads/api";
import { mayOpenShell } from "../../workloads/environment/shell";
import type { TaskGroup } from "../model";
import { RefreshNowButton } from "./RefreshNowButton";
import { ToolLink } from "./ToolLink";

// The tones of VpnStatusPill: green up, blue waiting for the sign-in, orange down, gray none.
const DOT: Record<VpnState, string> = { up: "bg-green", signing_in: "bg-blue", down: "bg-orange", absent: "bg-gray-400" };

function VpnTool({ environmentId, orgId }: { environmentId: string; orgId: string }) {
  const t = useT();
  const vpn = useQuery(environmentVpnQuery(environmentId));
  if (!vpn.data) return null;
  const state = vpnState(vpn.data);
  return (
    <ToolLink
      to={`/environments/${environmentId}/vpn?org=${orgId}`}
      label={t.vpn.link(t.vpn.state[state])}
      hint={t.tasks.group.toolbar.vpnHint}
      testId="toolbar-vpn"
      icon={
        <>
          <ShieldCheck weight="duotone" className="h-5 w-5" aria-hidden />
          <span
            className={cn("absolute end-1.5 top-1.5 h-2.5 w-2.5 rounded-full ring-2 ring-gray-0 dark:ring-gray-50", DOT[state])}
            data-state={state}
            aria-hidden
          />
        </>
      }
    />
  );
}

export function GroupToolbar({ group }: { group: TaskGroup }) {
  const { tasks, chat } = useT();
  const t = tasks.group.toolbar;
  const me = useQuery(accessQuery()).data;
  // A task group's org is always its owner's personal one (D52).
  const shell = mayOpenShell(me, group.org_id, true);
  const env = shell ? group.environment_id : null;
  const workspaces = useQuery({ ...environmentWorkspacesQuery(env ?? ""), enabled: !!env });
  const workspace = workspaces.data?.[0];
  const owner = me?.id === group.owner_user_id;
  const icon = "h-5 w-5";
  return (
    <div role="toolbar" aria-label={t.label} className="flex flex-wrap items-center gap-1">
      {env ? <ToolLink to={`/environments/${env}/terminal`} label={chat.terminals.vm} hint={chat.terminals.vmHint} icon={<Cube weight="duotone" className={icon} aria-hidden />} /> : null}
      {workspace ? (
        <ToolLink to={`/workspaces/${workspace.id}/terminal`} label={chat.terminals.workspace} hint={chat.terminals.workspaceHint} icon={<Terminal weight="duotone" className={icon} aria-hidden />} />
      ) : null}
      {owner && group.source !== "manual" && group.status !== "archived" ? <RefreshNowButton group={group} /> : null}
      {env ? <VpnTool environmentId={env} orgId={group.org_id} /> : null}
      <ToolLink to={`/task-groups/${group.id}/network`} label={t.network} hint={t.networkHint} icon={<GlobeHemisphereWest weight="duotone" className={icon} aria-hidden />} />
    </div>
  );
}
